  (function () {
    if (customElements.get('c-reels-carousel')) return;

    /*
      Copia de un slide para el bucle infinito. Tres cosas que hay que limpiar:
      - `data-block-id` / `data-shopify-*`: son la identidad del bloque para el
        editor de temas. Duplicarlas hace que el editor no sepa cual de las dos
        copias resaltar al seleccionar el bloque.
      - Foco: el clon queda `aria-hidden`, y contenido enfocable dentro de un
        `aria-hidden` es un fallo de accesibilidad — de ahi el `tabindex=-1`.
      - `preload="none"`: los clones no deben descargar video hasta que alguien
        los reproduzca. El `poster` que pone `video_tag` se sigue mostrando.
    */
    function cloneSlide(slide) {
      var clone = slide.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('data-block-id');
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');

      var all = [clone].concat(Array.prototype.slice.call(clone.querySelectorAll('*')));
      all.forEach(function (node) {
        Array.prototype.slice.call(node.attributes).forEach(function (attr) {
          if (attr.name.indexOf('data-shopify') === 0) node.removeAttribute(attr.name);
        });
      });

      Array.prototype.forEach.call(
        clone.querySelectorAll('a, button, [tabindex]'),
        function (node) {
          node.setAttribute('tabindex', '-1');
        }
      );

      Array.prototype.forEach.call(clone.querySelectorAll('video'), function (video) {
        video.setAttribute('preload', 'none');
      });

      return clone;
    }

    class ReelsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        this._loop = false;
        this._setWidth = 0;

        this._onScroll = this.handleScroll.bind(this);
        this._onResize = this.handleResize.bind(this);
        this._onClick = this.handleClick.bind(this);

        this.classList.add('c-video-reels__carousel--js');

        this.setupLoop();

        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize);
        this.addEventListener('click', this._onClick);

        this.setupDrag();

        if (this.dataset.autoplay === 'true' && 'IntersectionObserver' in window) {
          this.setupAutoplay();
        }
      }

      disconnectedCallback() {
        if (this.track) {
          this.track.removeEventListener('scroll', this._onScroll);
          this.teardownDrag();
        }
        window.removeEventListener('resize', this._onResize);
        this.removeEventListener('click', this._onClick);
        window.clearTimeout(this._scrollTimer);
        window.clearTimeout(this._resizeTimer);
        if (this._autoplayObserver) this._autoplayObserver.disconnect();
      }

      handleClick(event) {
        var toggle = event.target.closest('.c-video-reels__sound-toggle');
        if (toggle) {
          this.toggleSound(toggle);
        }
      }

      /*
        Arrastre con puntero. Solo para punteros de tipo mouse/pen: en touch el
        propio scroller nativo ya arrastra, y duplicarlo pelearia con el
        scroll-snap del navegador.

        Dos detalles que importan:
        - `is-dragging` apaga el scroll-snap via CSS. Sin eso, cada asignacion
          a scrollLeft es reencajada por el navegador y el track no se mueve.
        - Un listener de click en fase de CAPTURA sobre el track se traga el
          click posterior al arrastre. Hace falta capture (no bubble) porque
          quien escucha el click es el propio <video> (video-controls) y el
          <a> de la mini-card, ambos descendientes: en captura llegamos antes
          que ellos y stopPropagation() impide que el evento los alcance.
          Si no, soltar tras arrastrar pausaba el reel o navegaba al producto.
      */
      setupDrag() {
        var self = this;
        var track = this.track;

        this._dragging = false;
        this._dragMoved = false;
        this._suppressClick = false;
        this._pointerId = null;

        this._onPointerDown = function (event) {
          if (event.pointerType === 'touch' || event.button !== 0) return;
          if (self.getMaxScroll() <= 0) return;
          self._dragging = true;
          self._dragMoved = false;
          self._suppressClick = false;
          self._pointerId = event.pointerId;
          self._startX = event.clientX;
          self._startScroll = track.scrollLeft;
        };

        this._onPointerMove = function (event) {
          if (!self._dragging || event.pointerId !== self._pointerId) return;
          var delta = event.clientX - self._startX;
          if (!self._dragMoved) {
            if (Math.abs(delta) < 4) return;
            self._dragMoved = true;
            track.classList.add('is-dragging');
            try {
              track.setPointerCapture(self._pointerId);
            } catch (error) {
              /* setPointerCapture puede fallar si el puntero ya se solto */
            }
          }
          event.preventDefault();
          track.scrollLeft = self._startScroll - delta;
        };

        this._onPointerUp = function (event) {
          if (!self._dragging || event.pointerId !== self._pointerId) return;
          self._dragging = false;
          self._pointerId = null;
          if (!self._dragMoved) return;
          self._suppressClick = true;
          track.classList.remove('is-dragging');
          self.snapToNearest();
        };

        this._onClickCapture = function (event) {
          if (!self._suppressClick) return;
          self._suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        };

        this._onDragStart = function (event) {
          if (self._dragging) event.preventDefault();
        };

        track.classList.add('is-draggable');
        track.addEventListener('pointerdown', this._onPointerDown);
        track.addEventListener('pointermove', this._onPointerMove);
        track.addEventListener('pointerup', this._onPointerUp);
        track.addEventListener('pointercancel', this._onPointerUp);
        track.addEventListener('click', this._onClickCapture, true);
        track.addEventListener('dragstart', this._onDragStart);
      }

      teardownDrag() {
        var track = this.track;
        track.removeEventListener('pointerdown', this._onPointerDown);
        track.removeEventListener('pointermove', this._onPointerMove);
        track.removeEventListener('pointerup', this._onPointerUp);
        track.removeEventListener('pointercancel', this._onPointerUp);
        track.removeEventListener('click', this._onClickCapture, true);
        track.removeEventListener('dragstart', this._onDragStart);
      }

      /*
        Con bucle no se acota a getMaxScroll(): el track tiene tres juegos y
        `wrapLoop` ya mantiene el scroll dentro de la ventana central. Si el
        encaje suave cruza el umbral de reposicionamiento, el salto que dispara
        es de exactamente un juego, o sea a una posicion visualmente identica —
        no se nota.
      */
      snapToNearest() {
        var step = this.getStep();
        if (step <= 0) return;
        var target = Math.round(this.track.scrollLeft / step) * step;
        if (!this._loop) target = Math.min(this.getMaxScroll(), target);
        this.track.scrollTo({ left: target, behavior: 'smooth' });
      }

      toggleSound(toggle) {
        var slide = toggle.closest('.c-video-reels__slide');
        var video = slide && slide.querySelector('video');
        if (!video) return;
        video.muted = !video.muted;
        toggle.setAttribute('data-sound', video.muted ? 'off' : 'on');
      }

      getStep() {
        var slide = this.track.querySelector('.c-video-reels__slide');
        if (!slide) return this.track.clientWidth;
        var styles = window.getComputedStyle(this.track);
        var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return slide.getBoundingClientRect().width + gap;
      }

      getMaxScroll() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      /*
        Bucle infinito. El track sigue siendo un scroller nativo: se clona el
        juego completo de slides una vez ANTES y una vez DESPUES del original,
        se arranca posicionado sobre el juego real y, cuando el scroll se sale
        de esa ventana central, se reposiciona de golpe un juego entero.
        Como el salto es exactamente el ancho de un juego, la posicion
        equivalente y el punto de snap coinciden y el salto es invisible.

        Solo se clona si hay 2+ slides. Con una sola no hay nada que recorrer,
        y clonar un unico video para "dar la vuelta" seria mas coste que
        beneficio.
      */
      setupLoop() {
        // Si el elemento se desconecta y se vuelve a conectar (el editor de
        // temas mueve nodos), connectedCallback corre otra vez sobre el MISMO
        // DOM, que ya tiene clones. Sin esta guarda clonariamos los clones.
        if (this.track.querySelector('.c-video-reels__slide[data-clone]')) {
          this._loop = true;
          this._setCount = this.track.querySelectorAll(
            '.c-video-reels__slide:not([data-clone])'
          ).length;
          this.measureLoop();
          return;
        }

        var originals = Array.prototype.slice.call(
          this.track.querySelectorAll('.c-video-reels__slide')
        );
        if (originals.length < 2) return;

        var before = document.createDocumentFragment();
        var after = document.createDocumentFragment();
        originals.forEach(function (slide) {
          before.appendChild(cloneSlide(slide));
          after.appendChild(cloneSlide(slide));
        });
        this.track.appendChild(after);
        this.track.insertBefore(before, this.track.firstChild);

        this._loop = true;
        this._setCount = originals.length;
        this.measureLoop();
        this.track.scrollLeft = this._setWidth;
      }

      measureLoop() {
        this._setWidth = this._loop ? this.getStep() * this._setCount : 0;
      }

      /*
        Se llama en cada evento de scroll, sin debounce: hay que reposicionar
        ANTES de que el usuario vea el borde. Reasignar scrollLeft dispara otro
        evento de scroll, pero en esa segunda pasada ya estamos dentro de la
        ventana y no se reajusta nada, asi que no hay bucle.
      */
      wrapLoop() {
        if (!this._loop) return;
        // Si la seccion se monto sin layout (dentro de algo oculto), la medida
        // inicial salio 0. Se reintenta en cuanto hay scroll, que ya implica
        // que el track es visible.
        if (!this._setWidth) this.measureLoop();
        if (!this._setWidth) return;
        var left = this.track.scrollLeft;
        if (left < this._setWidth * 0.5) {
          this.track.scrollLeft = left + this._setWidth;
        } else if (left >= this._setWidth * 1.5) {
          this.track.scrollLeft = left - this._setWidth;
        }
      }

      handleScroll() {
        this.wrapLoop();
      }

      handleResize() {
        window.clearTimeout(this._resizeTimer);
        var self = this;
        this._resizeTimer = window.setTimeout(function () {
          // El gap cambia por breakpoint (12px movil / 24px desktop), asi que
          // el ancho de un juego cambia con el. Tras remedir hay que reencajar
          // por si la posicion actual quedo fuera de la ventana central.
          self.measureLoop();
          self.wrapLoop();
        }, 150);
      }

      setupAutoplay() {
        var videos = this.querySelectorAll('video');
        if (!videos.length) return;
        this._autoplayObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var video = entry.target;
              if (entry.isIntersecting) {
                video.play().catch(function () {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.6 }
        );
        var observer = this._autoplayObserver;
        videos.forEach(function (video) {
          observer.observe(video);
        });
      }
    }

    customElements.define('c-reels-carousel', ReelsCarousel);
  })();
